import { type ReactNode, useState } from 'react';
import type { Approacher } from '../types';
import {
  modeHandlers,
  useActions,
  useResetActions,
  useStreakResetActions,
  type Mode,
} from '../hooks/use-detail-actions';
import { ApproacherEditForm } from './approacher-edit-form';
import { DetailView, type DetailMode } from './approacher-detail-view';

type DetailProps = {
  a: Approacher;
  mode: DetailMode;
  setMode: (m: Mode) => void;
  pwd: string;
  actions: ReturnType<typeof useActions>;
  resetActions: ReturnType<typeof useResetActions>;
  streakActions: ReturnType<typeof useStreakResetActions>;
};

function Detail(p: DetailProps): ReactNode {
  const pending =
    p.actions.pending ||
    p.resetActions.resetPending ||
    p.streakActions.streakResetPending;
  return (
    <DetailView
      a={p.a}
      mode={p.mode}
      tempPwd={p.pwd}
      {...modeHandlers(p.setMode)}
      onConfirmArchive={p.actions.onConfirmArchive}
      onConfirmRestore={p.actions.onConfirmRestore}
      onConfirmReset={p.resetActions.onConfirmReset}
      onConfirmStreakReset={p.streakActions.onConfirmStreakReset}
      pending={pending}
    />
  );
}

function EditMode({
  a,
  onBack,
}: {
  a: Approacher;
  onBack: () => void;
}): ReactNode {
  return (
    <ApproacherEditForm approacher={a} onSave={onBack} onCancel={onBack} />
  );
}

export function ApproacherDetailContent({
  approacher: a,
  onClose,
}: {
  approacher: Approacher;
  onClose: () => void;
}): ReactNode {
  const [mode, setMode] = useState<Mode>('view');
  const [pwd, setPwd] = useState<string>('');
  const actions = useActions(a.id, onClose);
  const resetActions = useResetActions(a.id, { pwd, setPwd }, setMode);
  const streakActions = useStreakResetActions(a.id, setMode);
  if (mode === 'edit') return <EditMode a={a} onBack={() => setMode('view')} />;
  const detail = {
    a,
    mode: mode as DetailMode,
    setMode,
    pwd,
    actions,
    resetActions,
    streakActions,
  };
  return <Detail {...detail} />;
}
